import type { AccessRole } from '@/constants/rbac';
import { BULK_INTERNAL_ROLE_VALUES, getBulkInternalRolesForAccess } from '../constants';
import type { BulkImportError, BulkInternalRole, BulkPreviewData, BulkUserType } from '../types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isCsvFile(file: File): boolean {
  const name = file.name.trim().toLowerCase();
  return name.endsWith('.csv');
}

export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

export function getWhatsappDigits(value: string): string {
  return value.replace(/\D/g, '');
}

export function isValidWhatsapp(value: string): boolean {
  const digits = getWhatsappDigits(value);
  return digits.length >= 10 && digits.length <= 15;
}

export function isValidInternalRole(value: string): value is BulkInternalRole {
  return BULK_INTERNAL_ROLE_VALUES.includes(value as BulkInternalRole);
}

export function isAssignableBulkInternalRole(value: string, accessRole?: AccessRole): boolean {
  if (!isValidInternalRole(value)) return false;
  if (!accessRole) return true;
  return getBulkInternalRolesForAccess(accessRole).some((item) => item.value === value);
}

export function validateInternalRow(
  data: {
    nome: string;
    email: string;
    role: string;
  },
  accessRole?: AccessRole
): BulkImportError[] {
  const errors: BulkImportError[] = [];

  if (!data.nome.trim()) {
    errors.push({ field: 'nome', message: 'Nome é obrigatório', source: 'local' });
  }

  if (!data.email.trim()) {
    errors.push({ field: 'email', message: 'E-mail é obrigatório', source: 'local' });
  } else if (!isValidEmail(data.email)) {
    errors.push({ field: 'email', message: 'E-mail inválido', source: 'local' });
  }

  if (!data.role.trim()) {
    errors.push({ field: 'role', message: 'Role é obrigatória', source: 'local' });
  } else if (!isAssignableBulkInternalRole(data.role, accessRole)) {
    errors.push({ field: 'role', message: 'Role inválida', source: 'local' });
  }

  return errors;
}

export function validatePartnerRow(data: {
  nome: string;
  email: string;
  whatsapp: string;
}): BulkImportError[] {
  const errors: BulkImportError[] = [];

  if (!data.nome.trim()) {
    errors.push({ field: 'nome', message: 'Nome é obrigatório', source: 'local' });
  }

  if (!data.email.trim()) {
    errors.push({ field: 'email', message: 'E-mail é obrigatório', source: 'local' });
  } else if (!isValidEmail(data.email)) {
    errors.push({ field: 'email', message: 'E-mail inválido', source: 'local' });
  }

  if (!data.whatsapp.trim()) {
    errors.push({ field: 'whatsapp', message: 'WhatsApp é obrigatório', source: 'local' });
  } else if (!isValidWhatsapp(data.whatsapp)) {
    errors.push({
      field: 'whatsapp',
      message: 'WhatsApp deve conter entre 10 e 15 dígitos',
      source: 'local',
    });
  }

  return errors;
}

export function validateRowByType(
  userType: BulkUserType,
  data: BulkPreviewData,
  accessRole?: AccessRole
): BulkImportError[] {
  if (userType === 'internal') {
    const internalData = data as { nome: string; email: string; role: string };
    return validateInternalRow(internalData, accessRole);
  }

  const partnerData = data as { nome: string; email: string; whatsapp: string };
  return validatePartnerRow(partnerData);
}
